<template>
  <b-container class="py-4 two-factor-container">
    <b-row class="justify-content-center">
      <b-col lg="7">

        <!-- ===== MANDATORY MODE banner ===== -->
        <b-alert v-if="isMandatory" show variant="warning" class="mb-4">
          <b-icon icon="shield-lock" class="mr-2" />
          Для дальнейшей работы с платформой необходимо включить двухфакторную
          аутентификацию. Это разовая настройка.
        </b-alert>

        <!-- ===== Loading ===== -->
        <div v-if="loading" class="text-center py-5">
          <b-spinner /> <span class="ml-2">Загрузка…</span>
        </div>

        <!-- ============================================================
             STATUS BLOCK — only in settings mode
        ============================================================ -->
        <div v-else-if="isSettings && view === 'status'">
          <h4 class="mb-3">Двухфакторная аутентификация</h4>

          <b-card class="tf-card mb-3" v-if="status.enabled">
            <div class="d-flex align-items-center mb-3">
              <b-badge variant="success" pill class="mr-2">Включена</b-badge>
              <small class="text-muted" v-if="status.enabledAt">
                с {{ formatDate(status.enabledAt) }}
              </small>
            </div>
            <p class="mb-2">
              Осталось резервных кодов: <strong>{{ status.backupCodesLeft }}</strong>
            </p>
            <b-alert v-if="!status.canDisable" show variant="info" class="mt-3 mb-3">
              <b-icon icon="info-circle" class="mr-2" />
              Двухфакторная аутентификация обязательна и не может быть
              отключена. Если потеряли доступ к приложению — используйте
              резервный код, либо обратитесь в поддержку для сброса.
            </b-alert>
            <div class="mt-3">
              <b-button variant="primary" class="mr-2" @click="startEnroll">
                <b-icon icon="phone" class="mr-1" />Перенастроить устройство
              </b-button>
              <b-button variant="outline-warning" class="mr-2" @click="askRegenerateConfirm">
                Перегенерировать резервные коды
              </b-button>
              <b-button v-if="status.canDisable" variant="outline-danger" @click="view = 'disable'">
                Отключить 2FA
              </b-button>
            </div>
          </b-card>

          <b-card class="tf-card mb-3" v-else>
            <div class="d-flex align-items-center mb-3">
              <b-badge variant="secondary" pill class="mr-2">Не включена</b-badge>
            </div>
            <p class="mb-3 text-muted">
              Рекомендуем включить — это серьёзно повышает защиту аккаунта.
              Понадобится приложение-аутентификатор: Google Authenticator,
              Microsoft Authenticator, Authy, 1Password или любое
              совместимое (TOTP).
            </p>
            <b-button variant="primary" @click="startEnroll">
              Включить 2FA
            </b-button>
          </b-card>
        </div>

        <!-- ============================================================
             ENROLL — STEP 1: scan QR
        ============================================================ -->
        <div v-else-if="view === 'enroll-qr'">
          <h4 class="mb-3">
            {{ status.enabled ? 'Перенастройка устройства' : 'Шаг 1 из 2 — Сканируйте QR-код' }}
          </h4>
          <b-alert v-if="status.enabled" show variant="info" class="mb-3">
            <b-icon icon="info-circle" class="mr-2" />
            Старое приложение продолжит работать до подтверждения кода с нового
            устройства. После подтверждения старый секрет станет недействителен.
          </b-alert>
          <b-card class="tf-card">
            <p class="mb-3">
              Откройте приложение-аутентификатор (Google Authenticator,
              Microsoft Authenticator, Authy и др.) и отсканируйте QR-код
              ниже.
            </p>
            <div class="text-center mb-3">
              <img :src="setup.qrPng" alt="2FA QR" class="qr-image" />
            </div>
            <p class="mb-1 small text-muted">
              Если QR не считывается — введите секрет вручную:
            </p>
            <code class="d-block secret-box mb-3">{{ setup.secret }}</code>

            <b-form @submit.prevent="confirmCode">
              <label class="reg-label">Введите 6-значный код из приложения</label>
              <b-form-input
                v-model="confirmCodeInput"
                placeholder="000000"
                maxlength="6"
                autocomplete="one-time-code"
                inputmode="numeric"
                class="code-input mb-3"
                required
              />
              <div class="d-flex justify-content-between">
                <b-button v-if="isSettings" variant="link" @click="cancelEnroll">
                  Отмена
                </b-button>
                <b-button type="submit" variant="primary" :disabled="confirming">
                  <b-spinner v-if="confirming" small /> Подтвердить
                </b-button>
              </div>
            </b-form>
          </b-card>
        </div>

        <!-- ============================================================
             ENROLL — STEP 2: show backup codes
        ============================================================ -->
        <div v-else-if="view === 'enroll-backup'">
          <h4 class="mb-3">Шаг 2 из 2 — Сохраните резервные коды</h4>
          <b-alert show variant="warning">
            <b-icon icon="exclamation-triangle" class="mr-2" />
            Эти коды показаны <strong>один раз</strong>. Сохраните их в надёжное
            место (менеджер паролей, распечатанный лист). Каждый код можно
            использовать только один раз — они нужны если потеряете доступ к
            телефону с приложением.
          </b-alert>
          <b-card class="tf-card">
            <div class="backup-codes mb-3">
              <code v-for="(c, i) in backupCodes" :key="i" class="backup-code">{{ c }}</code>
            </div>
            <b-button variant="outline-secondary" class="mr-2" @click="copyBackupCodes">
              <b-icon icon="clipboard" class="mr-1" />Скопировать все
            </b-button>
            <b-button variant="success" @click="finishEnroll">
              Я сохранил, продолжить
            </b-button>
          </b-card>
        </div>

        <!-- ============================================================
             DISABLE — password + current code
        ============================================================ -->
        <div v-else-if="view === 'disable'">
          <h4 class="mb-3">Отключение 2FA</h4>
          <b-card class="tf-card">
            <p class="text-muted">
              Введите пароль и текущий код из приложения. После отключения
              войти в аккаунт можно будет только по паролю — рекомендуем
              включить 2FA снова при первой возможности.
            </p>
            <b-form @submit.prevent="performDisable">
              <label class="reg-label">Пароль</label>
              <b-form-input
                type="password"
                v-model="disableForm.password"
                class="mb-3"
                required
              />
              <label class="reg-label">Код из приложения</label>
              <b-form-input
                v-model="disableForm.code"
                placeholder="000000"
                maxlength="6"
                inputmode="numeric"
                class="code-input mb-3"
                required
              />
              <b-button variant="link" @click="view = 'status'">Отмена</b-button>
              <b-button type="submit" variant="danger" :disabled="busy">
                <b-spinner v-if="busy" small /> Отключить
              </b-button>
            </b-form>
          </b-card>
        </div>

        <!-- ============================================================
             REGENERATE BACKUP CODES — code only
        ============================================================ -->
        <div v-else-if="view === 'regenerate'">
          <h4 class="mb-3">Перегенерация резервных кодов</h4>
          <b-card class="tf-card">
            <p class="text-muted">
              Текущие коды будут аннулированы. Подтвердите кодом из
              приложения.
            </p>
            <b-form @submit.prevent="performRegenerate">
              <label class="reg-label">Код из приложения</label>
              <b-form-input
                v-model="regenerateCode"
                placeholder="000000"
                maxlength="6"
                inputmode="numeric"
                class="code-input mb-3"
                required
              />
              <b-button variant="link" @click="view = 'status'">Отмена</b-button>
              <b-button type="submit" variant="primary" :disabled="busy">
                <b-spinner v-if="busy" small /> Сгенерировать новые
              </b-button>
            </b-form>
          </b-card>
        </div>

        <!-- Confirmation before entering the regenerate-code view.
             Kept outside the v-if/v-else chain — Vue requires v-else to
             sit on the immediate next sibling, so putting a modal between
             the two status cards broke rendering when status.enabled was
             false. -->
        <b-modal
          v-model="confirmRegenerateOpen"
          title="Перегенерация резервных кодов"
          ok-title="Перегенерировать"
          cancel-title="Отмена"
          ok-variant="warning"
          cancel-variant="link"
          header-class="modal-header-dark"
          body-class="modal-body-dark"
          footer-class="modal-footer-dark"
          @ok="onRegenerateConfirmed"
        >
          <p class="mb-2">
            Все текущие резервные коды будут <strong>аннулированы</strong>.
            Использовать их для входа больше не получится.
          </p>
          <p class="mb-0 text-muted">
            Действие нельзя отменить. Подойдёт, если вы потеряли список или
            подозреваете, что он попал в чужие руки.
          </p>
        </b-modal>

      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import axios from 'axios';
import { apiDomain, getHeader } from '@/config/config';

export default {
  name: 'TwoFactor',

  props: {
    // 'mandatory' — entered from /2fa-setup right after login when the
    // backend returned requires_2fa_setup. The bearer is the pre_auth_token
    // saved in localStorage under 'pendingTwoFA'. On confirm we save the
    // real tokens and route on into the dashboard.
    //
    // 'settings'  — entered from Settings. The bearer is the regular access
    // JWT. Confirm just refreshes the view back to 'status'.
    mode: {
      type: String,
      default: 'settings',
      validator: v => ['mandatory', 'settings'].includes(v),
    },
  },

  data() {
    return {
      loading: true,
      // Status pulled from /api/2fa/status (settings mode only). For
      // mandatory mode we skip the status fetch and jump straight to enroll.
      status: { enabled: false, backupCodesLeft: 0, enabledAt: '', canDisable: false },
      view: 'status', // 'status' | 'enroll-qr' | 'enroll-backup' | 'disable' | 'regenerate'

      // Enroll state
      setup: { qrPng: '', secret: '', uri: '' },
      confirmCodeInput: '',
      confirming: false,
      backupCodes: [],

      // Disable / regenerate state
      busy: false,
      disableForm: { password: '', code: '' },
      regenerateCode: '',
      confirmRegenerateOpen: false,
    };
  },

  computed: {
    isMandatory() { return this.mode === 'mandatory'; },
    isSettings()  { return this.mode === 'settings'; },
    /**
     * Headers for 2FA endpoints — same shape as getHeader() but with the
     * pre_auth_token when in mandatory mode (the user doesn't have a real
     * JWT yet).
     */
    authHeaders() {
      if (this.isMandatory) {
        const pending = JSON.parse(window.localStorage.getItem('pendingTwoFA') || 'null');
        if (!pending || !pending.preAuthToken) return null;
        return { Accept: 'application/json', Authorization: 'Bearer ' + pending.preAuthToken };
      }
      return getHeader();
    },
  },

  mounted() {
    if (this.isMandatory) {
      if (!this.authHeaders) {
        // No pre_auth_token — user landed here directly. Send them to login.
        this.$router.replace('/');
        return;
      }
      this.startEnroll();
    } else {
      this.fetchStatus();
    }
  },

  methods: {
    formatDate(iso) {
      try { return new Date(iso).toLocaleString('ru-RU'); }
      catch (e) { return iso; }
    },

    // ---- status ----
    fetchStatus() {
      this.loading = true;
      axios
        .post(apiDomain + '/api/2fa/status', {}, { headers: this.authHeaders })
        .then(res => {
          this.status = res.data;
          this.view = 'status';
        })
        .catch(() => this.toast('Не удалось загрузить статус 2FA.', 'danger'))
        .finally(() => { this.loading = false; });
    },

    // ---- enroll: setup + confirm ----
    startEnroll() {
      this.confirmCodeInput = '';
      this.loading = true;
      axios
        .post(apiDomain + '/api/2fa/setup', {}, { headers: this.authHeaders })
        .then(res => {
          this.setup = res.data;
          this.view = 'enroll-qr';
        })
        .catch(() => this.toast('Не удалось получить QR-код. Попробуйте ещё раз.', 'danger'))
        .finally(() => { this.loading = false; });
    },
    confirmCode() {
      if (!this.confirmCodeInput || this.confirmCodeInput.length < 6) return;
      this.confirming = true;
      axios
        .post(
          apiDomain + '/api/2fa/confirm',
          { code: this.confirmCodeInput.trim() },
          { headers: this.authHeaders }
        )
        .then(res => {
          this.backupCodes = res.data.backupCodes || [];
          // Mandatory branch: backend returns real tokens — stash them now
          // so finishEnroll can complete the login.
          if (this.isMandatory && res.data.tokens) {
            const authUser = {
              access_token: res.data.tokens.access_token,
              refresh_token: res.data.tokens.refresh_token,
            };
            window.localStorage.setItem('authUser', JSON.stringify(authUser));
            window.localStorage.removeItem('pendingTwoFA');
          }
          this.view = 'enroll-backup';
        })
        .catch(err => {
          const msg = (err.response && err.response.data) || '';
          if (typeof msg === 'string' && msg.includes('invalid-code')) {
            this.toast('Код неверный. Проверьте, что часы на телефоне и сервере синхронизированы.', 'danger');
          } else {
            this.toast('Не удалось подтвердить код.', 'danger');
          }
        })
        .finally(() => { this.confirming = false; });
    },
    finishEnroll() {
      if (this.isMandatory) {
        // Tokens are already in localStorage from confirmCode().
        this.$router.replace({ name: 'AfterAuth' });
      } else {
        this.fetchStatus();
      }
    },
    cancelEnroll() {
      this.view = 'status';
      this.confirmCodeInput = '';
    },
    copyBackupCodes() {
      const text = this.backupCodes.join('\n');
      try {
        navigator.clipboard.writeText(text);
        this.toast('Резервные коды скопированы в буфер обмена.', 'success');
      } catch (e) {
        this.toast('Не удалось скопировать. Запишите коды вручную.', 'warning');
      }
    },

    // ---- disable ----
    performDisable() {
      this.busy = true;
      axios
        .post(
          apiDomain + '/api/2fa/disable',
          { password: this.disableForm.password, code: this.disableForm.code.trim() },
          { headers: this.authHeaders }
        )
        .then(() => {
          this.toast('2FA отключена.', 'success');
          this.disableForm = { password: '', code: '' };
          this.fetchStatus();
        })
        .catch(err => {
          const msg = (err.response && err.response.data) || '';
          if (typeof msg === 'string' && msg.includes('client-cannot-disable')) {
            this.toast('Для клиентов 2FA обязательна и не может быть отключена.', 'warning');
            this.view = 'status';
          } else if (typeof msg === 'string' && msg.includes('bad-password')) {
            this.toast('Неверный пароль.', 'danger');
          } else if (typeof msg === 'string' && msg.includes('invalid-code')) {
            this.toast('Неверный код из приложения.', 'danger');
          } else {
            this.toast('Не удалось отключить 2FA.', 'danger');
          }
        })
        .finally(() => { this.busy = false; });
    },

    // ---- regenerate ----
    askRegenerateConfirm() {
      this.confirmRegenerateOpen = true;
    },
    onRegenerateConfirmed() {
      this.regenerateCode = '';
      this.view = 'regenerate';
    },
    performRegenerate() {
      this.busy = true;
      axios
        .post(
          apiDomain + '/api/2fa/regenerate-backup-codes',
          { code: this.regenerateCode.trim() },
          { headers: this.authHeaders }
        )
        .then(res => {
          this.backupCodes = res.data.backupCodes || [];
          this.regenerateCode = '';
          this.view = 'enroll-backup'; // re-use the backup-codes display
        })
        .catch(err => {
          const msg = (err.response && err.response.data) || '';
          if (typeof msg === 'string' && msg.includes('invalid-code')) {
            this.toast('Неверный код из приложения.', 'danger');
          } else {
            this.toast('Не удалось перегенерировать коды.', 'danger');
          }
        })
        .finally(() => { this.busy = false; });
    },

    toast(msg, variant) {
      if (!this.$bvToast) return;
      this.$bvToast.toast(msg, {
        title: variant === 'danger' ? 'Ошибка' : variant === 'warning' ? 'Внимание' : 'Готово',
        variant,
        autoHideDelay: 5000,
        toaster: 'b-toaster-top-center',
        solid: true,
      });
    },
  },
};
</script>

<style scoped>
.two-factor-container { color: var(--Neutrals-8, #FCFCFD); }
.tf-card {
  background: var(--Shade-2-30, #18191D);
  border: 1px solid var(--Neutrals-3, #353945);
  color: var(--Neutrals-8, #FCFCFD);
}
.qr-image {
  width: 240px;
  height: 240px;
  background: #fff;
  padding: 8px;
  border-radius: 8px;
}
.secret-box {
  background: var(--Neutrals-2, #23262F);
  padding: 8px 12px;
  border-radius: 8px;
  word-break: break-all;
  color: var(--Primary-1, #EFB914);
  letter-spacing: 1px;
}
.code-input {
  font-size: 22px;
  letter-spacing: 6px;
  text-align: center;
  font-family: 'Inter', monospace;
}
.backup-codes {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
}
.backup-code {
  background: var(--Neutrals-2, #23262F);
  padding: 8px 12px;
  border-radius: 6px;
  font-family: 'Inter', monospace;
  font-size: 16px;
  text-align: center;
  letter-spacing: 2px;
}
.reg-label {
  color: var(--Neutrals-5, #B1B5C3);
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  display: block;
  margin-bottom: 6px;
}
</style>

<style>
/* The 2FA modal sits on the dark layout — Bootstrap's default white modal
   chrome looks out of place. Match the surrounding surfaces. */
.modal-header-dark,
.modal-body-dark,
.modal-footer-dark {
  background: var(--Shade-2-30, #18191D);
  color: var(--Neutrals-8, #FCFCFD);
  border-color: var(--Neutrals-3, #353945);
}
.modal-header-dark .close {
  color: var(--Neutrals-8, #FCFCFD);
  text-shadow: none;
  opacity: 0.7;
}
</style>
